import React, { useState } from "react";
import {
  Check,
  Minus,
  ChevronDown,
  ChevronUp,
  Info,
  CheckSquare,
  Square,
} from "lucide-react";
import formatPrice0 from "../../../../utility/formatPrice0";
import { offeringsService } from "../../../../services/api/settings";
import { OfferingAddon } from "../../../../store/types/settings";
import formatText from "../../../../utility/formatText";
import formatPrice from "../../../../utility/formatPrice";

import Skeleton from "../../../../components/elements/Skeleton";

interface AddOnFeature {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  billingType: string;
  isActive: boolean;
  expiresAt: string;
  activatedAt: string;
  STARTER: boolean;
  GROWTH: boolean;
  SCALE: boolean;
  ENTERPRISE: boolean;
  activation: string;
  serviceType?: string;
  planSpecificPricing?: {
    planId: string;
    price: number;
    currency?: string;
  }[];
  raw?: any;
}

interface AddOnsAiProps {
  organizationId?: string;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  showDirectAddOns?: boolean;
  selectedDirectIds?: string[];
  onDirectToggle?: (id: string) => void;
}

const AddOnsAi: React.FC<AddOnsAiProps> = ({
  organizationId,
  selectedIds = [],
  onToggle,
  showDirectAddOns,
  selectedDirectIds = [],
  onDirectToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addOns, setAddOns] = useState<AddOnFeature[]>([]);
  const [directAddOns, setDirectAddOns] = useState<AddOnFeature[]>([]);

  React.useEffect(() => {
    const fetchAddOns = async () => {
      setLoading(true);
      try {
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data) {
          // Map AI Add-ons (RECRUITER)
          if (response.data.RECRUITER?.addons) {
            const mappedAiAddOns: AddOnFeature[] =
              response.data.RECRUITER.addons.map((item: OfferingAddon) => ({
                id: item.id,
                name: item.name,
                displayName: item.name,
                description: "",
                price:
                  typeof item.price === "string"
                    ? parseFloat(item.price)
                    : item.price || 0,
                currency: item.currency,
                category: item.category,
                billingType: item.billingType,
                isActive: item.isActive,
                expiresAt: "",
                activatedAt: "",
                STARTER: true,
                GROWTH: true,
                SCALE: true,
                ENTERPRISE: true,
                activation: "Instant",
                serviceType: item.serviceType || "RECRUITER",
                planSpecificPricing:
                  item.raw?.planSpecificPricing ||
                  (item as any).planSpecificPricing,
                raw: item,
              }));
            setAddOns(mappedAiAddOns);
          }

          // Map Direct Add-ons (DIRECT) if showDirectAddOns is enabled
          if (showDirectAddOns && response.data.DIRECT?.addons) {
            const mappedDirectAddOns: AddOnFeature[] =
              response.data.DIRECT.addons.map((item: OfferingAddon) => ({
                id: item.id,
                name: item.name,
                displayName: item.name,
                description: "",
                price:
                  typeof item.price === "string"
                    ? parseFloat(item.price)
                    : item.price || 0,
                currency: item.currency,
                category: "DIRECT",
                billingType: item.billingType,
                isActive: item.isActive,
                expiresAt: "",
                activatedAt: "",
                STARTER: true,
                GROWTH: true,
                SCALE: true,
                ENTERPRISE: true,
                activation: "Instant",
                serviceType: item.serviceType || "DIRECT",
                planSpecificPricing:
                  item.raw?.planSpecificPricing ||
                  (item as any).planSpecificPricing,
                raw: item,
              }));
            setDirectAddOns(mappedDirectAddOns);
          }
        }
      } catch (error) {
        console.error("Failed to fetch add-ons", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAddOns();
  }, [organizationId, showDirectAddOns]);

  const renderStatus = (status: boolean) => {
    return status ? (
      <Check className="w-5 h-5 text-gray-800 mx-auto" />
    ) : (
      <Minus className="w-5 h-5 text-gray-400 mx-auto" />
    );
  };

  const getAiTierPrice = (
    feature: AddOnFeature,
    tier: "STARTER" | "GROWTH" | "SCALE" | "ENTERPRISE",
  ) => {
    const name = (feature.displayName || feature.name || "").toLowerCase();
    const isDirect =
      feature.category === "DIRECT" || feature.serviceType === "DIRECT";

    // 1. Map tier string to backend plan ID
    let planId = "";
    if (isDirect) {
      if (tier === "STARTER") planId = "STARTER";
      else if (tier === "GROWTH") planId = "GROWTH";
      else if (tier === "SCALE") planId = "SCALE";
      else if (tier === "ENTERPRISE") planId = "GROWTH";
    } else {
      if (tier === "STARTER") planId = "STARTER";
      else if (tier === "GROWTH") planId = "GROWTH";
      else if (tier === "SCALE") planId = "SCALE";
      else if (tier === "ENTERPRISE") planId = "VIP";
    }

    // 2. Find pricing record in backend data
    const pPricing = feature.planSpecificPricing?.find(
      (p) => p.planId?.toUpperCase() === planId,
    );

    // Helper to format price with zero decimals
    const formatIntPrice = (p: number) => {
      return new Intl.NumberFormat("en-NG", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(p);
    };

    // 3. Render prices according to sample layout or fallback
    if (isDirect) {
      // Direct pricing
      if (name.includes("credits") || name.includes("credits top-up")) {
        if (tier === "STARTER") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "150,000";
          return `${val}`;
        }
        if (tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "150,000";
          return `${val}`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "120,000";
          return `${val}`;
        }
        if (tier === "ENTERPRISE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "100,000";
          return `${val}`;
        }
      }

      if (name.includes("migration")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "800,000";
          return `${val}`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "600,000";
          return `${val}`;
        }
        return "Custom";
      }

      if (
        name.includes("cv upload") ||
        name.includes("cv upload boost") ||
        name.includes("bulk cv")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "300,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("sso") ||
        name.includes("saml") ||
        name.includes("sso / saml")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("build") ||
        name.includes("custom / GROWTH") ||
        name.includes("custom build")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") return "—";
        return "Contact Sales";
      }
    } else {
      // AI Recruiter pricing
      if (name.includes("interview") || name.includes("top-up")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "400,000";
          return `${val} (10 sessions)`;
        }
        if (tier === "SCALE") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "350,000";
          return `${val} (12 sessions)`;
        }
        return "Custom";
      }

      if (name.includes("video") || name.includes("storage")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (
        name.includes("branding") ||
        name.includes("white-label") ||
        name.includes("white label")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") {
          const val = pPricing ? formatIntPrice(pPricing.price) : "1,500,000";
          return `${val}`;
        }
        return "Included";
      }

      if (name.includes("tuning") || name.includes("model")) {
        if (tier === "STARTER" || tier === "GROWTH") {
          return pPricing && pPricing.price > 0
            ? `${formatIntPrice(pPricing.price)}`
            : "Per quote";
        }
        if (tier === "SCALE") {
          return "1 included";
        }
        return "Custom";
      }

      if (
        name.includes("build") ||
        name.includes("custom / GROWTH") ||
        name.includes("custom build")
      ) {
        if (tier === "STARTER" || tier === "GROWTH") return "—";
        return "Contact Sales";
      }
    }

    // Fallback to planSpecificPricing format if present
    if (pPricing) {
      return `${formatIntPrice(pPricing.price)}`;
    }

    return "Custom";
  };

  const isSelectable = (f: any) => {
    return f.isActive !== false;
  };

  return (
    <section id="direct-addons" className="pb-2 px-2 lg:px-3">
      <div className="max-w-[950px] mx-auto">
        <div className="bg-white rounded-[15px] shadow-sm border border-primary overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors rounded-[15px]"
          >
            <h3 className="text-[15px] md:text-[17px] font-medium text-[#212121]">
              Add-Ons
            </h3>
            {isOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-500" />
            )}
          </button>

          {isOpen && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 w-[50px] text-center">
                      {(onToggle || onDirectToggle) && (
                        <div
                          className="flex items-center justify-center cursor-pointer"
                          onClick={() => {
                            const selectableAddOns = addOns.filter((f) =>
                              isSelectable(f),
                            );
                            const allAddOnIds = selectableAddOns.map(
                              (f) => f.id,
                            );
                            const allDirectAddOnIds = directAddOns
                              .filter((f) => isSelectable(f))
                              .map((f) => f.id);

                            const allAddOnsSelected =
                              allAddOnIds.length > 0 &&
                              allAddOnIds.every((id) =>
                                selectedIds.includes(id),
                              );
                            const allDirectSelected =
                              !showDirectAddOns ||
                              (allDirectAddOnIds.length > 0 &&
                                allDirectAddOnIds.every((id) =>
                                  selectedDirectIds.includes(id),
                                ));

                            const allSelected =
                              allAddOnsSelected && allDirectSelected;

                            if (allSelected) {
                              allAddOnIds.forEach((id) => {
                                if (selectedIds.includes(id) && onToggle)
                                  onToggle(id);
                              });
                              allDirectAddOnIds.forEach((id) => {
                                if (
                                  selectedDirectIds.includes(id) &&
                                  onDirectToggle
                                )
                                  onDirectToggle(id);
                              });
                            } else {
                              allAddOnIds.forEach((id) => {
                                if (!selectedIds.includes(id) && onToggle)
                                  onToggle(id);
                              });
                              allDirectAddOnIds.forEach((id) => {
                                if (
                                  !selectedDirectIds.includes(id) &&
                                  onDirectToggle
                                )
                                  onDirectToggle(id);
                              });
                            }
                          }}
                        >
                          {(() => {
                            const selectableAddOns = addOns.filter((f) =>
                              isSelectable(f),
                            );
                            const selectableDirect = directAddOns.filter((f) =>
                              isSelectable(f),
                            );

                            const allAddOnIds = selectableAddOns.map(
                              (f) => f.id,
                            );
                            const allDirectIds = selectableDirect.map(
                              (f) => f.id,
                            );

                            const allAddOnsSelected =
                              allAddOnIds.length > 0 &&
                              allAddOnIds.every((id) =>
                                selectedIds.includes(id),
                              );
                            const allDirectSelected =
                              !showDirectAddOns ||
                              allDirectIds.length === 0 ||
                              allDirectIds.every((id) =>
                                selectedDirectIds.includes(id),
                              );

                            const allSelected =
                              allAddOnsSelected && allDirectSelected;
                            return allSelected ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                            );
                          })()}
                        </div>
                      )}
                    </th>
                    <th className="text-left px-6 py-3 text-[14px] font-medium text-[#212121] w-[35%]">
                      Services
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      STARTER
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Enterprise
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Enterprise Plus
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      VIP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-3 py-4 text-center">
                            <Skeleton
                              variant="rectangular"
                              height={20}
                              width={20}
                              className="mx-auto rounded"
                            />
                          </td>
                          <td className="px-3 py-4">
                            <Skeleton variant="text" height={16} width="80%" />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                        </tr>
                      ))
                    : addOns
                        .map((feature, index) => {
                          const isSelected = selectedIds.includes(feature.id);
                          const canSelect = isSelectable(feature);
                          return (
                            <tr
                              key={feature.id}
                              className={`hover:bg-gray-50 transition-colors ${
                                isSelected ? "bg-blue-50/50" : ""
                              }`}
                              onClick={() =>
                                canSelect && onToggle && onToggle(feature.id)
                              }
                            >
                              <td className="px-3 py-3 text-center cursor-pointer">
                                {onToggle && canSelect && (
                                  <div className="flex items-center justify-center">
                                    {isSelected ? (
                                      <CheckSquare className="w-5 h-5 text-blue-600" />
                                    ) : (
                                      <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-[14px] text-[#616161]">
                                <div className="flex items-center gap-1 group relative">
                                  {feature.displayName}
                                  {feature.price > 0 && (
                                    <>
                                      <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                      <div className="invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                          <div className="text-gray-500 text-xs">
                                            Price
                                          </div>
                                          <div className="text-gray-900 font-normal text-right">
                                            {formatPrice0(
                                              feature.price,
                                              feature.currency,
                                            )}
                                          </div>
                                          <div className="text-gray-500 text-xs">
                                            Billing Type
                                          </div>
                                          <div className="text-gray-900 font-normal text-right">
                                            {formatText(feature.billingType)}
                                          </div>
                                          <div className="text-gray-500 text-xs">
                                            Activation
                                          </div>
                                          <div className="text-gray-900 font-normal text-right">
                                            {feature.activation}
                                          </div>
                                        </div>
                                        <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                {getAiTierPrice(feature, "STARTER")}
                              </td>
                              <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                {getAiTierPrice(feature, "GROWTH")}
                              </td>
                              <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                {getAiTierPrice(feature, "SCALE")}
                              </td>
                              <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                {getAiTierPrice(feature, "ENTERPRISE")}
                              </td>
                            </tr>
                          );
                        })
                        .concat(
                          showDirectAddOns && directAddOns.length > 0
                            ? [
                                <tr
                                  key="direct-header"
                                  className="bg-gray-50 border-y border-gray-200"
                                >
                                  <td
                                    colSpan={6}
                                    className="px-6 py-2 text-[15px] font-bold text-[#212121]"
                                  >
                                    Direct Add-Ons
                                  </td>
                                </tr>,
                                ...directAddOns.map((feature, index) => {
                                  const isSelected = selectedDirectIds.includes(
                                    feature.id,
                                  );
                                  const canSelect = isSelectable(feature);
                                  return (
                                    <tr
                                      key={feature.id}
                                      className={`hover:bg-gray-50 transition-colors ${
                                        isSelected ? "bg-blue-50/50" : ""
                                      }`}
                                      onClick={() =>
                                        canSelect &&
                                        onDirectToggle &&
                                        onDirectToggle(feature.id)
                                      }
                                    >
                                      <td className="px-3 py-3 text-center cursor-pointer">
                                        {onDirectToggle && canSelect && (
                                          <div className="flex items-center justify-center">
                                            {isSelected ? (
                                              <CheckSquare className="w-5 h-5 text-blue-600" />
                                            ) : (
                                              <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-3 py-3 text-[14px] text-[#616161]">
                                        <div className="flex items-center gap-1 group relative">
                                          {feature.displayName}
                                          {feature.price > 0 && (
                                            <>
                                              <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                              <div className="invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                  <div className="text-gray-500 text-xs">
                                                    Price
                                                  </div>
                                                  <div className="text-gray-900 font-normal text-right">
                                                    {formatPrice0(
                                                      feature.price * 1000,
                                                      feature.currency,
                                                    )}
                                                  </div>
                                                  <div className="text-gray-500 text-xs">
                                                    Billing Type
                                                  </div>
                                                  <div className="text-gray-900 font-normal text-right">
                                                    {formatText(
                                                      feature.billingType,
                                                    )}
                                                  </div>
                                                  <div className="text-gray-500 text-xs">
                                                    Activation
                                                  </div>
                                                  <div className="text-gray-900 font-normal text-right">
                                                    {feature.activation}
                                                  </div>
                                                </div>
                                                <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                        {getAiTierPrice(feature, "STARTER")}
                                      </td>
                                      <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                        {getAiTierPrice(feature, "GROWTH")}
                                      </td>
                                      <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                        {getAiTierPrice(feature, "SCALE")}
                                      </td>
                                      <td className="px-6 py-3 text-center text-[13px] font-medium text-gray-700">
                                        {getAiTierPrice(feature, "ENTERPRISE")}
                                      </td>
                                    </tr>
                                  );
                                }),
                              ]
                            : [],
                        )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AddOnsAi;
